import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Search } from 'lucide-react';
import { countriesData } from '../data/countries';
import { fetchGeoLocation, submitLead, getCountryFromGeo, validatePhoneNumber, buildLeadPayload } from '../services/api';
import '../styles/ConsultationForm.css';

export const ConsultationForm = ({ isOpen, onClose, formType = 'consultation' }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: 'US',
    moreInfo: ''
  });

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const dropdownRef = useRef(null);
  const countryButtonRef = useRef(null);

  // Detect country by IP on mount
  useEffect(() => {
    detectCountryByIP();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        countryButtonRef.current &&
        !countryButtonRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown]);

  // Close form on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const detectCountryByIP = async () => {
    try {
      const geoResponse = await fetchGeoLocation();
      
      if (geoResponse?.isSuccess) {
        setGeoData(geoResponse);
        const countryInfo = getCountryFromGeo(geoResponse);
        const country = countriesData.find(c => c.code === countryInfo.code);
        
        if (country) {
          setSelectedCountry(country);
          setFormData(prev => ({ ...prev, countryCode: country.code }));
        }
      }
    } catch (error) {
      console.log('IP detection failed, using default US');
    } finally {
      // Default fallback to US
      if (!selectedCountry) {
        const defaultCountry = countriesData.find(c => c.code === 'US');
        setSelectedCountry(defaultCountry);
        setFormData(prev => ({ ...prev, countryCode: 'US' }));
      }
      setIsDetectingCountry(false);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setFormData(prev => ({ ...prev, countryCode: country.code }));
    setShowCountryDropdown(false);
    setSearchQuery('');
  };

  const filteredCountries = countriesData.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.phone_code.includes(searchQuery)
  );

  const popularCountries = filteredCountries.filter(c => c.popular);
  const allCountries = filteredCountries.filter(c => !c.popular);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate phone number in real-time
    if (name === 'phone' && selectedCountry) {
      const validation = validatePhoneNumber(value, selectedCountry);
      setPhoneError(validation.isValid ? '' : validation.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    // Validate phone number before submission
    if (selectedCountry && formData.phone) {
      const validation = validatePhoneNumber(formData.phone, selectedCountry);
      if (!validation.isValid) {
        setPhoneError(validation.message);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      // Prepare lead data using buildLeadPayload helper
      const leadData = {
        name: formData.fullName,
        email: formData.email,
        countryDialCode: selectedCountry?.phone_code || '1',
        phoneNumber: formData.phone,
        description: formData.moreInfo || 'Consultation request'
      };

      // Build proper payload with all required fields
      const payload = buildLeadPayload(leadData, geoData);

      // Submit lead
      const result = await submitLead(payload);

      if (result.success && result.statusCode === 200) {
        // Redirect to application page with customer ID
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        // Show error message
        setErrorMessage(result.message);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const formTitle = formType === 'consultation' ? 'Request Free Consultation' : 'Start Trademark Registration';
  const formDescription = formType === 'consultation' 
    ? 'Fill out the form below and our IP specialists will contact you within 24 hours.'
    : 'Begin your trademark registration process by providing your details below.';

  return (
    <div className="consultation-form-overlay">
      <div className="consultation-form-modal">
        <button 
          className="close-button"
          onClick={onClose}
          aria-label="Close form"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="form-header">
          <h2>{formTitle}</h2>
          <p>{formDescription}</p>
        </div>

        <form onSubmit={handleSubmit} className="consultation-form">
          {/* Error Message */}
          {errorMessage && (
            <div style={{
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              color: '#c33',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {errorMessage}
            </div>
          )}

          {/* Full Name */}
          <div className="form-group">
            <label htmlFor="fullName">
              Full Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="John Doe"
              required
            />
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="john@example.com"
              required
            />
          </div>

          {/* Phone Number with Country Selector */}
          <div className="form-group">
            <label htmlFor="phone">
              Phone Number <span className="required">*</span>
            </label>
            <div className="intel-phone-input">
              <div className="phone-input-container">
                <button
                  ref={countryButtonRef}
                  type="button"
                  className={`country-selector-btn ${isDetectingCountry ? 'loading' : ''}`}
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  disabled={isDetectingCountry}
                >
                  {isDetectingCountry ? (
                    <span className="detecting-text">...</span>
                  ) : (
                    <>
                      <span className="selected-flag">{selectedCountry?.flag}</span>
                      <span className="selected-code">{selectedCountry?.phone_code}</span>
                      <ChevronDown className={`dropdown-arrow ${showCountryDropdown ? 'open' : ''}`} />
                    </>
                  )}
                </button>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                  required
                  disabled={isDetectingCountry}
                />
              </div>

              {showCountryDropdown && (
                <div ref={dropdownRef} className="country-dropdown">
                  <div className="country-search-container">
                    <Search className="search-icon" />
                    <input
                      type="text"
                      className="country-search"
                      placeholder="Search countries..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="country-list">
                    {popularCountries.length > 0 && (
                      <div className="country-group">
                        <div className="group-title">Popular Countries</div>
                        {popularCountries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            className={`country-item ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                            onClick={() => handleCountrySelect(country)}
                          >
                            <span className="country-flag">{country.flag}</span>
                            <span className="country-name">{country.name}</span>
                            <span className="country-code">{country.phone_code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {allCountries.length > 0 && (
                      <div className="country-group">
                        <div className="group-title">All Countries (A-Z)</div>
                        {allCountries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            className={`country-item ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                            onClick={() => handleCountrySelect(country)}
                          >
                            <span className="country-flag">{country.flag}</span>
                            <span className="country-name">{country.name}</span>
                            <span className="country-code">{country.phone_code}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {filteredCountries.length === 0 && (
                      <div className="no-results">
                        No countries found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {phoneError && (
              <div style={{
                color: '#dc3545',
                fontSize: '14px',
                marginTop: '4px'
              }}>
                {phoneError}
              </div>
            )}
            <small className="help-text">We'll never share your phone number</small>
          </div>

          {/* More Information */}
          <div className="form-group">
            <label htmlFor="moreInfo">
              What are you looking to protect? <span className="required">*</span>
            </label>
            <textarea
              id="moreInfo"
              name="moreInfo"
              value={formData.moreInfo}
              onChange={handleInputChange}
              placeholder="Tell us about your trademark, copyright, patent, or business formation needs..."
              rows="4"
              required
            />
            <small className="help-text">
              Provide as much detail as possible to help us understand your needs
            </small>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting || isDetectingCountry}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </button>

          <p className="privacy-notice">
            By submitting this form, you agree to our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Service</a>.
          </p>
        </form>
      </div>
    </div>
  );
};
